import Configs from '@/Configs';

import { normalizeHexColor } from '@/views/settings/company/core/normalizeHexColor';

import { resolveLegacyBrandClassToHex } from './legacyBrandColorMap';

/** KincodeButton theme: `light` = dark text on brand fill; `dark` = white text on brand fill. */
export type KincodeButtonTheme = 'light' | 'dark';

/**
 * Resolves stored or draft hex to a normalized `#RRGGBB` brand color.
 *
 * @param colorHex - `personalization.colorHex` (optional).
 * @param fallback - Default when missing or invalid.
 */
export function resolveCompanyBrandHex(
  colorHex?: string | null,
  fallback: string = Configs.companyColor
): string {
  return (
    normalizeHexColor(colorHex ?? '') ??
    normalizeHexColor(fallback) ??
    '#BBF7D0'
  );
}

/**
 * YIQ perceived brightness for sRGB hex (0–255). Used for light vs dark foreground on brand fills.
 *
 * @param hex - Normalized `#RRGGBB`.
 */
export function getYiqPerceivedBrightness(hex: string): number {
  const normalized = normalizeHexColor(hex);
  if (!normalized) {
    return 0;
  }

  const r = parseInt(normalized.slice(1, 3), 16);
  const g = parseInt(normalized.slice(3, 5), 16);
  const b = parseInt(normalized.slice(5, 7), 16);

  return (r * 299 + g * 587 + b * 114) / 1000;
}

/** YIQ threshold: below = white text; at/above = dark text (common UI convention). */
const YIQ_LIGHT_BACKGROUND_THRESHOLD = 128;

/**
 * WCAG relative luminance for sRGB hex (0–1).
 *
 * @param hex - Normalized `#RRGGBB`.
 */
export function getRelativeLuminance(hex: string): number {
  const normalized = normalizeHexColor(hex);
  if (!normalized) {
    return 0;
  }

  const toLinear = (channel: number) =>
    channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;

  const r = toLinear(parseInt(normalized.slice(1, 3), 16) / 255);
  const g = toLinear(parseInt(normalized.slice(3, 5), 16) / 255);
  const b = toLinear(parseInt(normalized.slice(5, 7), 16) / 255);

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Picks KincodeButton theme from brand hex (replaces stored `personalization.theme`).
 * Uses YIQ perceived brightness — handles saturated brand colors better than WCAG luminance alone.
 *
 * @param hex - Brand background color.
 */
export function getKincodeButtonThemeFromHex(hex: string): KincodeButtonTheme {
  return getYiqPerceivedBrightness(hex) >= YIQ_LIGHT_BACKGROUND_THRESHOLD ? 'light' : 'dark';
}

/**
 * Tailwind text class for labels/icons on top of a brand-colored surface.
 *
 * @param hex - Brand background color.
 */
export function getBrandForegroundTextClass(hex: string): 'text-kc-black' | 'text-white' {
  return getKincodeButtonThemeFromHex(hex) === 'light' ? 'text-kc-black' : 'text-white';
}

/**
 * @param companyPreferences - Loaded company preferences from `useData()`.
 */
export function getCompanyBrandFromPreferences(
  companyPreferences?: {
    personalization?: {
      colorHex?: string | null;
      color?: string | null;
    } | null;
  } | null
): { colorHex: string; buttonTheme: KincodeButtonTheme } {
  const personalization = companyPreferences?.personalization;
  const fromHex = normalizeHexColor(personalization?.colorHex ?? '');
  const fromLegacy = resolveLegacyBrandClassToHex(personalization?.color);
  const colorHex = resolveCompanyBrandHex(fromHex ?? fromLegacy ?? undefined);
  return {
    colorHex,
    buttonTheme: getKincodeButtonThemeFromHex(colorHex),
  };
}
